import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '@/api/client'
import { useT } from '@/i18n'
import { bonusMessages } from '@/i18n/messages/bonus'
import { commonMessages } from '@/i18n/messages/common'

export interface BonusConfig {
  normPerHour: number
  mealPerWorkerDay: number
  crewLeaderBonus: number
  newWorkerBonus: number
  newWorkerWindowDays: number
}

const INPUT =
  'w-full rounded-lg border border-neutral-300 px-2.5 py-1.5 text-sm dark:border-neutral-700 dark:bg-neutral-950'

/**
 * The bonus thresholds — norm, meal allowance and bonus amounts. Seeded from input.xlsx and
 * edited here so the calculation can be matched to AU without a code change. Never touches AU.
 */
export function BonusConfigDialog({ config, onClose }: { config: BonusConfig; onClose: () => void }) {
  const t = useT(bonusMessages)
  const tc = useT(commonMessages)
  const qc = useQueryClient()
  const [f, setF] = useState<BonusConfig>(config)
  const [error, setError] = useState<string | null>(null)
  const set = (k: keyof BonusConfig) => (v: string) => setF((p) => ({ ...p, [k]: Number(v) }))

  const save = useMutation({
    mutationFn: () => api.put('/api/bonus/config', f),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['bonus'] }); onClose() },
    onError: (e) => setError(e instanceof ApiError ? e.message : t('couldNotSave')),
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
         role="dialog" aria-modal="true" onClick={onClose}>
      <div className="max-h-[min(88vh,100dvh-2rem)] w-full max-w-md overflow-y-auto rounded-2xl bg-white shadow-xl ring-1 ring-black/5 dark:bg-neutral-900 dark:ring-white/10"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-2 border-b border-neutral-200 p-5 dark:border-neutral-800">
          <div>
            <h2 className="text-lg font-semibold">{t('settingsTitle')}</h2>
            <p className="mt-0.5 text-sm text-neutral-500">{t('settingsSubtitle')}</p>
          </div>
          <button onClick={onClose} aria-label={tc('close')}
                  className="grid size-10 shrink-0 place-items-center rounded-xl text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700 dark:hover:bg-neutral-800 dark:hover:text-neutral-200">✕</button>
        </div>

        <div className="space-y-3 p-5">
          <Field label={t('normTarget')} hint={t('normTargetHint')}>
            <input type="number" step="0.1" className={INPUT} value={f.normPerHour} onChange={(e) => set('normPerHour')(e.target.value)} />
          </Field>
          <Field label={t('mealAllowance')} hint='"Topli obrok"'>
            <input type="number" step="0.5" className={INPUT} value={f.mealPerWorkerDay} onChange={(e) => set('mealPerWorkerDay')(e.target.value)} />
          </Field>
          <Field label={t('crewLeaderBonus')} hint='"Bonus za vođu kolone"'>
            <input type="number" step="0.5" className={INPUT} value={f.crewLeaderBonus} onChange={(e) => set('crewLeaderBonus')(e.target.value)} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t('newWorkerBonus')} hint='"Bonus za nove radnike"'>
              <input type="number" step="0.5" className={INPUT} value={f.newWorkerBonus} onChange={(e) => set('newWorkerBonus')(e.target.value)} />
            </Field>
            <Field label={t('newWorkerWindow')} hint={t('newWorkerWindowHint')}>
              <input type="number" className={INPUT} value={f.newWorkerWindowDays} onChange={(e) => set('newWorkerWindowDays')(e.target.value)} />
            </Field>
          </div>
          <p className="text-xs text-neutral-400">
            {t('newWorkerNote')}
          </p>
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-neutral-200 p-5 dark:border-neutral-800">
          {error ? <p role="alert" className="text-sm" style={{ color: 'var(--viz-critical)' }}>{error}</p> : <span />}
          <div className="flex gap-2">
            <button onClick={onClose}
                    className="rounded-lg px-3 py-1.5 text-sm text-neutral-600 ring-1 ring-neutral-300 hover:bg-neutral-100 dark:text-neutral-300 dark:ring-neutral-700 dark:hover:bg-neutral-800">{tc('cancel')}</button>
            <button onClick={() => { setError(null); save.mutate() }} disabled={save.isPending}
                    className="rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-500 disabled:opacity-60">
              {save.isPending ? t('saving') : tc('save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-neutral-600 dark:text-neutral-300">{label}</span>
      <div className="mt-1">{children}</div>
      {hint && <span className="mt-0.5 block text-[11px] text-neutral-400">{hint}</span>}
    </label>
  )
}
